import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api.ts';
import {
    MessageSquare,
    Trash2,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    AlertTriangle,
    User,
    Star,
    Sparkles,
    Bug,
    Lightbulb,
    HelpCircle
} from 'lucide-react';

interface Suggestion {
    _id: string;
    feedback: string;
    username: string;
    userId: string;
    rating: number;
    category: string;
    createdAt: string;
}

interface Pagination {
    page: number;
    limit: number;
    total: number;
    pages: number;
}

const AdminSuggestions: React.FC = () => {
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 50, total: 0, pages: 0 });
    const [loading, setLoading] = useState(true);
    const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [activeCategory, setActiveCategory] = useState<string>('');

    const categories = [
        { id: '', label: 'Todo', icon: <MessageSquare size={16} />, color: '#a855f7' },
        { id: 'general', label: 'General', icon: <MessageSquare size={16} />, color: '#4ecdc4' },
        { id: 'feature', label: 'Feature', icon: <Sparkles size={16} />, color: '#ff9f43' },
        { id: 'bug', label: 'Bugs', icon: <Bug size={16} />, color: '#ff6b6b' },
        { id: 'character', label: 'Personajes', icon: <User size={16} />, color: '#45aaf2' },
        { id: 'other', label: 'Otros', icon: <HelpCircle size={16} />, color: '#a5b1c2' }
    ];

    const fetchSuggestions = async (page = 1, category = activeCategory) => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: page.toString(),
                limit: '50',
                category
            });

            const response = await fetch(`${API_URL}/admin/suggestions?${params}`, {
                credentials: 'include'
            });
            const data = await response.json();

            if (data.success) {
                setSuggestions(data.suggestions);
                setPagination(data.pagination);
            }
        } catch (error) {
            console.error('Error fetching suggestions:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSuggestions();
    }, [activeCategory]);

    const handleDeleteSuggestion = async (suggestionId: string) => {
        try {
            setActionLoading(true);
            const response = await fetch(`${API_URL}/admin/suggestions/${suggestionId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if ((await response.json()).success) {
                fetchSuggestions(pagination.page);
                setShowDeleteModal(false);
                setSelectedSuggestion(null);
            }
        } catch (error) {
            console.error('Error deleting suggestion:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getCategoryStyles = (catId: string) => {
        const cat = categories.find(c => c.id === catId);
        return {
            color: cat?.color || '#a5b1c2',
            icon: cat?.icon || <HelpCircle size={16} />,
            label: cat?.label || catId
        };
    };

    return (
        <div>
            {/* Header */}
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
                        Sugerencias y Feedback
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.5)' }}>
                        {pagination.total} sugerencias recibidas
                    </p>
                </div>
                <button
                    onClick={() => fetchSuggestions(pagination.page)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 20px',
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        color: 'white',
                        cursor: 'pointer'
                    }}
                >
                    <RefreshCw size={18} />
                    Actualizar
                </button>
            </div>

            {/* Filter Tabs */}
            <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '24px',
                overflowX: 'auto',
                paddingBottom: '8px'
            }}>
                {categories.map((cat) => (
                    <button
                        key={cat.id}
                        onClick={() => setActiveCategory(cat.id)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            background: activeCategory === cat.id ? cat.color : 'rgba(255,255,255,0.1)',
                            border: 'none',
                            borderRadius: '10px',
                            color: activeCategory === cat.id ? 'white' : 'rgba(255,255,255,0.6)',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s ease',
                            fontWeight: activeCategory === cat.id ? '600' : '400'
                        }}
                    >
                        {cat.icon}
                        {cat.label}
                    </button>
                ))}
            </div>

            {/* Suggestions List */}
            <div style={{
                background: 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(20px)',
                borderRadius: '20px',
                border: '1px solid rgba(255,255,255,0.1)',
                overflow: 'hidden'
            }}>
                {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                        <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
                    </div>
                ) : suggestions.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                        <Lightbulb size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                        <p>No hay sugerencias encontradas en esta categoría</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {suggestions.map((item, index) => {
                            const catInfo = getCategoryStyles(item.category);
                            return (
                                <div
                                    key={item._id}
                                    style={{
                                        padding: '24px',
                                        borderBottom: index < suggestions.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                                        display: 'flex',
                                        gap: '20px'
                                    }}
                                >
                                    {/* Content */}
                                    <div style={{ flex: 1 }}>
                                        {/* Header */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            marginBottom: '12px'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                color: '#4ecdc4'
                                            }}>
                                                <User size={16} />
                                                <span style={{ fontWeight: '600' }}>{item.username}</span>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '4px 10px',
                                                background: `${catInfo.color}20`,
                                                border: `1px solid ${catInfo.color}40`,
                                                borderRadius: '8px',
                                                color: catInfo.color,
                                                fontSize: '12px',
                                                fontWeight: '600'
                                            }}>
                                                {catInfo.icon}
                                                {catInfo.label.toUpperCase()}
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                color: '#f1c40f'
                                            }}>
                                                {[...Array(5)].map((_, i) => (
                                                    <Star
                                                        key={i}
                                                        size={14}
                                                        fill={i < item.rating ? '#f1c40f' : 'transparent'}
                                                        stroke={i < item.rating ? '#f1c40f' : 'rgba(255,255,255,0.2)'}
                                                    />
                                                ))}
                                            </div>

                                            <span style={{
                                                marginLeft: 'auto',
                                                color: 'rgba(255,255,255,0.4)',
                                                fontSize: '13px'
                                            }}>
                                                {formatDate(item.createdAt)}
                                            </span>
                                        </div>

                                        {/* Feedback Text */}
                                        <p style={{
                                            color: 'rgba(255,255,255,0.85)',
                                            fontSize: '15px',
                                            lineHeight: '1.6',
                                            padding: '16px',
                                            background: 'rgba(0,0,0,0.2)',
                                            borderRadius: '16px',
                                            margin: 0,
                                            whiteSpace: 'pre-wrap'
                                        }}>
                                            {item.feedback}
                                        </p>
                                    </div>

                                    {/* Actions */}
                                    <button
                                        onClick={() => { setSelectedSuggestion(item); setShowDeleteModal(true); }}
                                        style={{
                                            padding: '10px',
                                            background: 'rgba(255, 107, 107, 0.2)',
                                            border: 'none',
                                            borderRadius: '10px',
                                            color: '#ff6b6b',
                                            cursor: 'pointer',
                                            alignSelf: 'flex-start'
                                        }}
                                        title="Eliminar feedback"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '24px',
                        borderTop: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        <button
                            onClick={() => fetchSuggestions(pagination.page - 1)}
                            disabled={pagination.page === 1}
                            style={{
                                padding: '8px 14px',
                                background: 'rgba(255,255,255,0.1)',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                                opacity: pagination.page === 1 ? 0.5 : 1
                            }}
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                            Página {pagination.page} de {pagination.pages}
                        </span>
                        <button
                            onClick={() => fetchSuggestions(pagination.page + 1)}
                            disabled={pagination.page === pagination.pages}
                            style={{
                                padding: '8px 14px',
                                background: 'rgba(255,255,255,0.1)',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                cursor: pagination.page === pagination.pages ? 'not-allowed' : 'pointer',
                                opacity: pagination.page === pagination.pages ? 0.5 : 1
                            }}
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedSuggestion && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                        borderRadius: '24px',
                        padding: '32px',
                        width: '450px',
                        border: '1px solid rgba(255, 107, 107, 0.3)',
                        textAlign: 'center'
                    }}>
                        <AlertTriangle size={48} style={{ color: '#ff6b6b', marginBottom: '16px' }} />
                        <h2 style={{ color: 'white', fontSize: '20px', marginBottom: '16px' }}>¿Eliminar Feedback?</h2>

                        <div style={{
                            background: 'rgba(0,0,0,0.2)',
                            borderRadius: '12px',
                            padding: '16px',
                            marginBottom: '24px',
                            textAlign: 'left'
                        }}>
                            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '8px' }}>
                                Sugerencia de <strong>{selectedSuggestion.username}</strong>:
                            </p>
                            <p style={{ color: 'rgba(255,255,255,0.85)', margin: 0, fontSize: '14px' }}>
                                "{selectedSuggestion.feedback.substring(0, 150)}{selectedSuggestion.feedback.length > 150 ? '...' : ''}"
                            </p>
                        </div>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    background: 'rgba(255,255,255,0.1)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: '12px',
                                    color: 'white',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleDeleteSuggestion(selectedSuggestion._id)}
                                disabled={actionLoading}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                                    border: 'none',
                                    borderRadius: '12px',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                {actionLoading ? 'Eliminando...' : 'Eliminar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default AdminSuggestions;
